import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { PublicEventCard } from '@/components/public/PublicEventCard';
import { useProducer } from '@/hooks/useProducer';
import { usePublicProducerEvents } from '@/hooks/usePublicProducerEvents';
import { useCoverImageUrl } from '@/hooks/useMediaAssets';
import { isPastActivity } from '@/lib/events/isPastActivity';
import { resolvePublicMediaUrl } from '@/lib/media/resolvePublicMediaUrl';
import { activityDetailPath, producerPublicPath } from '@/lib/paths';

const MAX_ITEMS = 12;

export default function ProducerActivitiesEmbedPage() {
  const { slug } = useParams<{ slug: string }>();
  const { producer, loading: producerLoading } = useProducer(slug || '');
  const { data: events, isLoading: eventsLoading } = usePublicProducerEvents(producer?.id);
  const producerCoverUrl = useCoverImageUrl('producer', producer?.id ?? null, 1280);

  const upcomingEvents = useMemo(() => {
    return (events ?? [])
      .filter((event) => {
        return !isPastActivity({
          event_type: event.event_type,
          timing_mode: (event.timing_mode || 'date_time') as 'date_time' | 'open' | 'anytime' | 'season',
          start_date: event.next_occurrence_at || event.start_date,
          end_date: event.end_date,
        });
      })
      .slice(0, MAX_ITEMS);
  }, [events]);

  const isLoading = producerLoading || eventsLoading;

  if (isLoading) {
    return (
      <main className="min-h-screen bg-background/40 p-3 sm:p-4">
        <div className="grid gap-3">
          <Skeleton className="h-56 rounded-xl" />
          <Skeleton className="h-56 rounded-xl" />
          <Skeleton className="h-56 rounded-xl" />
        </div>
      </main>
    );
  }

  if (!producer || producer.status !== 'public') {
    return (
      <main className="min-h-screen bg-background/40 p-4">
        <section className="rounded-xl border border-border/40 bg-background/80 p-6 text-center">
          <p className="text-sm text-muted-foreground">Inga kommande aktiviteter just nu.</p>
        </section>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background/40 p-3 sm:p-4">
      <section className="space-y-3">
        {upcomingEvents.length > 0 ? (
          <div className="grid gap-3 sm:grid-cols-2">
            {upcomingEvents.map((event) => (
              <PublicEventCard
                key={event.event_id}
                className="shadow-none"
                showTypeBadge={false}
                event={{
                  id: event.event_id,
                  title: event.title,
                  startDate: event.next_occurrence_at || event.start_date,
                  endDate: event.end_date,
                  timingMode: (event.timing_mode || 'date_time') as 'date_time' | 'open' | 'anytime' | 'season',
                  outingTags: event.outing_tags,
                  producerName: producer.name,
                  producerCity: producer.city,
                  location: event.venue_text ?? event.location,
                  eventType: (event.event_type as 'event' | 'course') || 'event',
                  includesFarmSale: event.includes_farm_sale,
                  includesFarmSaleAlcohol: event.includes_farm_sale_alcohol,
                  occurrencesCount: event.occurrences_count,
                }}
                coverImageUrl={resolvePublicMediaUrl(event.cover_image_url)}
                fallbackCoverUrl={producerCoverUrl}
                onClick={() => {
                  window.open(activityDetailPath(event.event_id), '_blank', 'noopener,noreferrer');
                }}
              />
            ))}
          </div>
        ) : (
          <section className="rounded-xl border border-border/40 bg-background/80 p-6 text-center space-y-2">
            <p className="text-sm text-muted-foreground">Inga kommande aktiviteter just nu.</p>
            <Link
              className="inline-flex text-sm text-primary underline-offset-4 hover:underline"
              target="_blank"
              rel="noopener noreferrer"
              to={producerPublicPath(producer.slug || producer.id)}
            >
              Se gården på Farmly
            </Link>
          </section>
        )}
      </section>
    </main>
  );
}
